import { useEffect, useRef } from "react";
import type { VideoItem } from "../shared/types";

export function ReelsGrid({
  videos,
  onSelect,
  hasNextPage = false,
  isLoadingMore = false,
  onLoadMore,
}: {
  videos: VideoItem[];
  onSelect: (index: number) => void;
  /** Whether more videos are available to fetch. */
  hasNextPage?: boolean;
  /** True while the next page is being fetched. */
  isLoadingMore?: boolean;
  /** Called when the sentinel at the end of the grid scrolls into view. */
  onLoadMore?: () => void;
}) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasNextPage || !onLoadMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onLoadMore();
      },
      { rootMargin: "400px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, onLoadMore]);

  return (
    <div className="seeen-reels-grid">
      {videos.map((video, i) => (
        <button
          key={video.id}
          type="button"
          className="seeen-reels-cell"
          onClick={() => onSelect(i)}
          aria-label={video.title}
        >
          {video.poster ? (
            <img src={video.poster} alt="" loading="lazy" />
          ) : (
            <div style={{ width: "100%", height: "100%", background: "#27272a" }} />
          )}
          <div className="seeen-reels-hover" aria-hidden="true">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
            <span className="seeen-reels-title">{video.title}</span>
          </div>
        </button>
      ))}
      {isLoadingMore &&
        Array.from({ length: 3 }, (_, i) => (
          <div key={`skeleton-${i}`} className="seeen-reels-skeleton-cell" />
        ))}
      {hasNextPage && (
        <div ref={sentinelRef} className="seeen-reels-sentinel" aria-hidden="true" />
      )}
    </div>
  );
}
